import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef } from '@angular/core';
import { GridCustomTemplate, INgxGridFilter, INgxGridResult } from 'ngx-primeng-grid';
import { DialogService } from 'primeng/dynamicdialog';
import { INgxCrudLayout, INgxCrudLayoutSettingManageActionConfig, NgxCrudLayoutSetting_ManageConfig_Mode } from '../../_models/_crud-layout';
import { BaseComponent } from '../../../core/_base/base';
import { NgxUtility } from '../../../core/_utility/utility';
import { INgxFilter } from '../../../core/_interfaces/iresult';
import { INgxCrudLayoutDialogData } from '../../_models/_crud-manage';
import { IBreadcrumb } from '../../../ngx-wrapper-shared/models/breadcrumb';
import { ConfirmationService } from 'primeng/api';
import * as _ from 'lodash';
import { NgxCrudManageDialogComponent } from '../ngx-crud-manage-dialog/ngx-crud-manage-dialog.component';

@Component({
  selector: 'ngx-crud-grid',
  templateUrl: './ngx-crud-grid.component.html',
  styleUrls: ['./ngx-crud-grid.component.css']
})
export class NgxCrudGridComponent extends BaseComponent implements OnInit, AfterContentInit, OnDestroy {

  // custom column template instances
  @ContentChildren(GridCustomTemplate) templates: QueryList<GridCustomTemplate>;
  // layout instance
  _layout: INgxCrudLayout;
  // loading indicator
  _loading: boolean = true;

  @Input()
  set layout(value: INgxCrudLayout) {
    if (value) {
      this._layout = value;
    }
  }
  // breadcrumbs
  @Input() breadcrumbs: IBreadcrumb[] = [];
  // on grid ready
  @Output() onReady = new EventEmitter<INgxCrudLayout>();
  // custom template
  _customColumnTemplate: TemplateRef<any>;
  // dialog key
  _dialogKey: string;
  // filter
  _filter: INgxFilter<any>;
  // result
  _result: INgxGridResult<any> = { results: [], total: 0 }

  constructor(private dialog: DialogService,
    private confirmDialog: ConfirmationService) {
    super();
  }

  ngOnInit(): void {
    // init dialog key
    this._dialogKey = `crud_grid_layout_cd_${NgxUtility.getUniqueString()}`;
    // inti filter
    this.initFilter();
  }

  ngAfterContentInit() {
    if (this.templates) {
      this.templates.forEach((item) => {
        switch (item.getType()) {
          case 'custom': {
            this._customColumnTemplate = item.template;
          } break;
        }
      });
    }
  }

  // handle when user click on add button
  handleActionClick(action: INgxCrudLayoutSettingManageActionConfig) {
    // check for type
    if (action.mode === NgxCrudLayoutSetting_ManageConfig_Mode.modal) {
      this.openManageDialog({ layout: this._layout, model: null });
    } else {
      // fire action
      this._layout.setting.actionConfig.onAction(action);
    }
  }

  // get action dialog from the setting
  private getModalActionAction(): INgxCrudLayoutSettingManageActionConfig {
    const actionConfig = this._layout.setting.actionConfig;
    return actionConfig.actions.find(m => m.mode == NgxCrudLayoutSetting_ManageConfig_Mode.modal);
  }

  // open manage dialog
  private openManageDialog(data: INgxCrudLayoutDialogData<any>) {
    // get action config
    const action = this.getModalActionAction();
    if (action) {
      const dialogRef = this.dialog.open(NgxCrudManageDialogComponent, {
        header: action.modalConfig.title,
        width: action.modalConfig.width,
        contentStyle: NgxUtility.dialogContentStyle(),
        data
      });
      const ref1 = dialogRef.onClose.subscribe(result => {
        ref1.unsubscribe();
        if (result) {
          // search result
          this.lodResult();
        }
      });
    } else {
      throw 'Invalid Modal Action';
    }
  }

  // when table filter changed
  handleFilterChanged(filter: INgxGridFilter) {
    // set filter
    const reqFilter = _.cloneDeep(filter);
    // only set basic filter from grid
    this._layout.setting.gridConfig.action.filter.pageNo = reqFilter.pageNo;
    this._layout.setting.gridConfig.action.filter.perPage = reqFilter.perPage;
    this._layout.setting.gridConfig.action.filter.sortField = reqFilter.sortField;
    this._layout.setting.gridConfig.action.filter.sortOrder = reqFilter.sortOrder;
    // fire filter
    this._layout.setting.gridConfig.action.onFilterChanged(this._layout.setting.gridConfig.action.filter);
    // fire load result
    this.lodResult();
  }

  // handle search
  handleSearch(text: string) {
    // set filter
    this._layout.setting.gridConfig.action.filter.pageNo = 0;
    this._layout.setting.gridConfig.action.filter.searchText = text;
    // fire load result
    this.lodResult();
  }

  // for edit action
  onActionEdit(row: any) {
    const modalAction = this.getModalActionAction();
    if (modalAction) {
      this.openManageDialog({ model: row, layout: this._layout });
    } else {
      this.invokeGridAction('edit', row);
    }
  }

  // for delete action
  onActionDelete(row: any) {
    this.confirmDialog.confirm({
      header: 'Confirmation',
      message: 'Are you sure that you want to delete?',
      key: this._dialogKey,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.invokeGridAction('delete', row);
      }
    })
  }

  // invoke grid action
  private invokeGridAction(actionName: string, data: any) {
    if (this._layout.setting.gridConfig.action.customAction.invoke) {
      this._layout.setting.gridConfig.action.customAction.invoke(actionName, data);
    }
  }

  // init filter
  private initFilter() {
    this._filter = {
      pageNo: 0,
      perPage: this._layout.setting.gridConfig.perPage,
      sortField: this._layout.setting.gridConfig.sortField,
      sortOrder: this._layout.setting.gridConfig.sortOrder,
      formFilter: null,
      searchText: null
    };
    // set filter
    this._layout.setting.gridConfig.action['filter'] = this._filter;
    // call on ready
    this.onReady.emit(this._layout);
    // load result
    this.lodResult();
  }

  // load results
  private lodResult() {
    this._loading = true;
    this._layout.setting.gridConfig.action.loadResult(result => {
      // set result
      this._result = result;
      // hide loading
      this._loading = false;
    });
  }

  ngOnDestroy() {
    this.dispose();
  }

}
